/**
 * @fileoverview Client-side region cookie hydration for the consent banner.
 * @description One-shot fetch when middleware did not stamp `adsights_region_cr`
 * (e.g. cacheable redirect hops). Keeps geo out of the root layout.
 *
 * @module lib/analytics/hydrate-region-cookie
 */

import { invalidateClientContextCache } from "./client-context"
import { applyHydratedConsentRequiredDefault } from "./consent-eea-hydration-default"
import {
  CONSENT_ANALYTICS_EXPLICIT_COOKIE,
  CONSENT_ANALYTICS_MIRROR_COOKIE,
  REGION_CONSENT_REQUIRED_COOKIE,
} from "./cookie-names"
import { CONSENT_STORAGE_KEY } from "./consent"
import type { RegionSignalPayload } from "./region-signal"

/** Dedupes only the in-flight fetch — failures are retried on the next call. */
let inFlightHydration: Promise<RegionHydrationOutcome> | null = null

/** Last hydration API consent-required result (covers blocked Set-Cookie). */
let hydratedConsentRequired: boolean | null = null

/** Whether the last hydration pass confirmed region (false = fetch failed / geo unknown). */
let hydratedRegionResolved: boolean | null = null

/**
 * Result of {@link hydrateRegionCookieIfMissing}.
 *
 * @typedef {Object} RegionHydrationOutcome
 * @property {boolean} consentRequired - EEA/UK/CH default-deny applies.
 * @property {boolean} regionResolved - False when geo is unknown or the fetch failed.
 */
export type RegionHydrationOutcome = Readonly<{
  consentRequired: boolean
  regionResolved: boolean
}>

const RESOLVED_CONSENT_REQUIRED: RegionHydrationOutcome = {
  consentRequired: true,
  regionResolved: true,
}

const RESOLVED_REST_OF_WORLD: RegionHydrationOutcome = {
  consentRequired: false,
  regionResolved: true,
}

const UNRESOLVED_REGION: RegionHydrationOutcome = {
  consentRequired: false,
  regionResolved: false,
}

/**
 * Whether the region-signal API last reported a consent-required region.
 *
 * @returns {boolean} True when hydration returned `consentRequired: true`.
 */
export function getHydratedConsentRequiredSignal(): boolean {
  return hydratedConsentRequired === true
}

/**
 * Whether the last hydration pass failed or returned unknown geo.
 *
 * @returns {boolean} True when the last hydration pass failed or returned unknown geo.
 */
export function getHydratedRegionUnresolved(): boolean {
  return hydratedRegionResolved === false
}

/**
 * Whether a hydration pass has CONFIRMED the region (either way). Used by the
 * RUM pre-bind early flush (r3547669004) to distinguish "region known —
 * rest-of-world" (transmission clearly permitted) from "region still unknown
 * or fetch failed" (never transmit at pagehide, where deferral isn't
 * possible).
 *
 * @returns {boolean} True when the region-signal API confirmed the geo.
 */
export function getHydratedRegionResolved(): boolean {
  return hydratedRegionResolved === true
}

function hasRegionConsentCookie(): boolean {
  if (typeof document === "undefined") return false
  try {
    return (
      document.cookie.includes(`${REGION_CONSENT_REQUIRED_COOKIE}=1`) ||
      document.cookie.includes(`${REGION_CONSENT_REQUIRED_COOKIE}=0`)
    )
  } catch {
    return false
  }
}

function hasPersistedConsentChoice(): boolean {
  if (typeof document === "undefined") return false
  try {
    if (localStorage.getItem(CONSENT_STORAGE_KEY)) return true
  } catch {
    // ignore
  }
  try {
    const header = document.cookie
    return (
      header.includes(`${CONSENT_ANALYTICS_EXPLICIT_COOKIE}=1`) ||
      header.includes(`${CONSENT_ANALYTICS_EXPLICIT_COOKIE}=0`)
    )
  } catch {
    return false
  }
}

/**
 * Ensures the region-consent cookie is present by calling the region-signal API
 * when middleware did not stamp it. Retries after failure; dedupes concurrent calls.
 *
 * @returns {Promise<RegionHydrationOutcome>} Hydration result for consent gating.
 */
export async function hydrateRegionCookieIfMissing(): Promise<RegionHydrationOutcome> {
  if (typeof window === "undefined") return UNRESOLVED_REGION
  if (hasRegionConsentCookie()) {
    const consentRequired = document.cookie.includes(`${REGION_CONSENT_REQUIRED_COOKIE}=1`)
    hydratedConsentRequired = consentRequired
    hydratedRegionResolved = true
    if (!consentRequired) {
      clearStaleDefaultDenyMirrorIfRestOfWorld()
      invalidateClientContextCache()
    }
    return consentRequired ? RESOLVED_CONSENT_REQUIRED : RESOLVED_REST_OF_WORLD
  }
  // Set-Cookie blocked or already fetched — reuse the in-memory result.
  if (hydratedRegionResolved === true) {
    return hydratedConsentRequired === true
      ? RESOLVED_CONSENT_REQUIRED
      : RESOLVED_REST_OF_WORLD
  }
  inFlightHydration ??= fetchRegionSignal().finally(() => {
    inFlightHydration = null
  })
  return inFlightHydration
}

/**
 * Clears a stale bootstrap default-deny mirror once rest-of-world geo is
 * confirmed. Includes GPC visitors: a legacy non-explicit `mirror=0` written by
 * the old bootstrap (when GPC still denied first-party analytics) would
 * otherwise keep `edgeAttributionWriteAllowed` denying first-party edge
 * attribution for a rest-of-world GPC visitor until the cookie expires (~400d),
 * even though GPC no longer gates first-party analytics. Only the non-explicit
 * mirror is cleared — an explicit opt-out (`adsights_consent_explicit`) is left
 * untouched.
 *
 * @returns {void}
 */
function clearStaleDefaultDenyMirrorIfRestOfWorld(): void {
  if (typeof document === "undefined") return
  if (hasPersistedConsentChoice()) return
  try {
    const header = document.cookie
    if (!header.includes(`${CONSENT_ANALYTICS_MIRROR_COOKIE}=0`)) return
    if (
      header.includes(`${CONSENT_ANALYTICS_EXPLICIT_COOKIE}=1`) ||
      header.includes(`${CONSENT_ANALYTICS_EXPLICIT_COOKIE}=0`)
    ) {
      return
    }
    const secure = window.location.protocol === "https:" ? "; Secure" : ""
    document.cookie = `${CONSENT_ANALYTICS_MIRROR_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax${secure}`
  } catch {
    // ignore
  }
}

async function fetchRegionSignal(): Promise<RegionHydrationOutcome> {
  try {
    const res = await fetch("/api/analytics/region-signal", {
      credentials: "same-origin",
      cache: "no-store",
    })
    if (!res.ok) {
      hydratedConsentRequired = null
      hydratedRegionResolved = false
      return UNRESOLVED_REGION
    }
    const payload = (await res.json()) as RegionSignalPayload
    if (!payload.regionKnown) {
      hydratedConsentRequired = null
      hydratedRegionResolved = false
      return UNRESOLVED_REGION
    }
    hydratedConsentRequired = payload.consentRequired === true
    hydratedRegionResolved = true
    invalidateClientContextCache()
    if (payload.consentRequired) {
      applyHydratedConsentRequiredDefault()
    }
    if (
      payload.regionKnown &&
      hasRegionConsentCookie() &&
      !document.cookie.includes(`${REGION_CONSENT_REQUIRED_COOKIE}=1`)
    ) {
      clearStaleDefaultDenyMirrorIfRestOfWorld()
    }
    return payload.consentRequired ? RESOLVED_CONSENT_REQUIRED : RESOLVED_REST_OF_WORLD
  } catch {
    hydratedConsentRequired = null
    hydratedRegionResolved = false
    return UNRESOLVED_REGION
  }
}

/** Clears the in-flight hydration promise (tests only). */
export function resetRegionCookieHydrationForTests(): void {
  inFlightHydration = null
  hydratedConsentRequired = null
  hydratedRegionResolved = null
}

/** Sets the in-memory hydration signal (tests only). */
export function setHydratedConsentRequiredForTests(value: boolean | null): void {
  hydratedConsentRequired = value
}

/** Sets the in-memory region-resolved signal (tests only). */
export function setHydratedRegionResolvedForTests(value: boolean | null): void {
  hydratedRegionResolved = value
}
